import { useEffect } from "react";
import { averageColor } from "../lib/background.js";

/**
 * 画面全体の背景画像。
 *
 * body の background-attachment: fixed は iOS Safari で効かず、長いページでは
 * 画像がページ全体の高さまで引き伸ばされる。そのため画面に固定した要素を
 * 最背面に1枚置き、そこへ画像を敷く。
 *
 * 背景の上に直接文字が乗る箇所（読み込み中・得点の集計先の注記など）は、
 * 暗い背景でも読めるよう styles.css の「背景画像」の節で下地を付けている。
 * その指定はこの要素との兄弟関係（.backdrop ~ .app）で効かせているため、
 * 各画面の外側（#root の直下）に置くこと。
 *
 * iPhone の Safari は画面下のツールバーの裏にページの地の色（body の背景色）を出す。
 * 既定の地の色（薄いグレー）のままだと、暗い画像の下に灰色の帯が見えるため、
 * 画像の下端の色を読み取って地の色をそれに合わせる。
 * html ではなく body に付けるのは、html に背景色があると body の背景が
 * ページ全体へ広がらなくなり、body の箱が背景画像の手前に塗られてしまうため。
 */
export function Backdrop({ url }: { url: string | null }): React.ReactElement | null {
  useEffect(() => {
    if (url === null || typeof document === "undefined") return;
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      const color = bottomEdgeColor(img);
      if (color !== null) document.body.style.backgroundColor = color;
    };
    img.src = url;
    return () => {
      cancelled = true;
      document.body.style.backgroundColor = "";
    };
  }, [url]);

  if (url === null) return null;
  return (
    <div
      className="backdrop"
      aria-hidden="true"
      data-testid="backdrop"
      style={{ backgroundImage: `url("${url}")` }}
    />
  );
}

/**
 * 画像の下端（下から 8%）の平均色。読み取れなければ null（地の色は既定のまま）。
 * 画像は同じオリジンから配信しているため、キャンバスから画素を読み出せる。
 */
function bottomEdgeColor(img: HTMLImageElement): string | null {
  try {
    const width = 32;
    const height = Math.max(1, Math.round((img.naturalHeight / img.naturalWidth) * width));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (ctx === null) return null;
    ctx.drawImage(img, 0, 0, width, height);
    const rows = Math.max(1, Math.round(height * 0.08));
    const data = ctx.getImageData(0, height - rows, width, rows).data;
    return averageColor(data);
  } catch {
    return null;
  }
}
